import type {ReactElement} from 'react';
import React from 'react';
import {updateSidebarState} from '@Components/poster-editor/poster-editor-reducer';
import {ContextualMenuSkeleton} from '@Components/contextual-menu/components/contextual-menu-skeleton';
import {AnimatePresence, motion} from 'framer-motion';
import {ContextualOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/contextual-options';
import {PosterEditorErrorBoundary} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editor-error-boundary';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './poster-editing-side-panel.module.scss';
import {useShowContextualSkeletons} from '@Hooks/poster-editor/useShowContextualSkeletons';
import {useGetNumberOfPages} from '@Hooks/poster-editor/useGetNumberOfPages';
import {useGetNumberOfActiveItems} from '@Hooks/poster-editor/useGetNumberOfActiveItems';

export function PosterEditingSidePanel(): ReactElement<any> | null {
  const showSkeleton = useShowContextualSkeletons();
  const isSidebarOpen = useAppSelector((state) => {
    return state.posterEditor.isSidebarOpen;
  });
  const isSidebarSmall = useAppSelector((state) => {
    return state.posterEditor.isSidebarSmall;
  });
  const isPickingModeEnabled = useAppSelector((state) => {
    return state.posterEditor.isPickingModeEnabled;
  });
  const numberOfActiveItems = useGetNumberOfActiveItems();
  const numOfPages = useGetNumberOfPages();
  const isAudioPlaylistSelected = useAppSelector((state) => {
    return state.posterEditor.posterObject?.audioClips.selectedAudioItemUID !== '';
  });

  const dispatch = useAppDispatch();

  if (showSkeleton && isSidebarSmall && !isSidebarOpen) {
    return null;
  }

  if (showSkeleton) {
    return (
      <div key="poster-editor-skeleton" className={styles.editingPanel}>
        <ContextualMenuSkeleton />
      </div>
    );
  }

  const getContextualOptions = (): ReactElement<any> => {
    return (
      <PosterEditorErrorBoundary>
        <ContextualOptions key="poster-editor-contextual-options" />
      </PosterEditorErrorBoundary>
    );
  };

  const removeBgClasses = (): boolean => {
    return numOfPages > 1 && numberOfActiveItems === 0 && !isAudioPlaylistSelected;
  };

  if (!isSidebarSmall) {
    return (
      <div className={`js-blurred-during-onboarding ${styles.editingPanel} ${removeBgClasses() ? styles.noBg : ''} ${isPickingModeEnabled ? styles.pickingEnabled : ''}`}>
        {getContextualOptions()}
      </div>
    );
  }

  const toggleCollapseState = (): void => {
    dispatch(updateSidebarState(!isSidebarOpen));
  };

  const getCollapsedVersion = (): ReactElement<any> => {
    return (
      <ClickableDiv onClick={toggleCollapseState} key="1" className={`${styles.collapsedEditingPanel} flex-center`}>
        <Icon className="content-disabled" icon="icon-menu-dots" size={IconSize.SIZE_ICON_16} />
      </ClickableDiv>
    );
  };

  const collapsableSidebarContent = (): ReactElement<any> => {
    return (
      <AnimatePresence initial={false} mode="wait">
        {isSidebarOpen ? (
          <motion.div className={styles.editingPanelMotionDiv} key="1" initial={{opacity: 0}} animate={{opacity: 1}} transition={{duration: 0.1}} exit={{opacity: 0}}>
            {getContextualOptions()}
          </motion.div>
        ) : null}
        {!isSidebarOpen ? (
          <motion.div className={styles.editingPanelMotionDiv} key="2" initial={{opacity: 0}} animate={{opacity: 1}} transition={{duration: 0.1}} exit={{opacity: 0}}>
            {getCollapsedVersion()}
          </motion.div>
        ) : null}
      </AnimatePresence>
    );
  };

  return (
    <>
      <ClickableDiv
        onClick={toggleCollapseState}
        className={`flex-center ${styles.collapseButton} ${isSidebarOpen ? '' : styles.collapsed} ${isPickingModeEnabled ? styles.pickingEnabled : ''}`}
      >
        <Icon className={`${styles.collapseIcon} ${isSidebarOpen ? '' : styles.closed}`} icon="icon-caret-right" size={IconSize.SIZE_ICON_20} />
      </ClickableDiv>
      <div className={`${styles.editingPanel} ${styles.collapsable} ${isSidebarOpen ? styles.smallEditorShadow : styles.collapsed}`}>{collapsableSidebarContent()}</div>
      <div className={styles.sidebarSpacer} />
    </>
  );
}
